import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAudioPlayer } from "@/context/AudioContext";
import { radioStations, type RadioStation } from "@/mocks/radios";
import Swal from "sweetalert2";

function formatCount(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

/* ── Derive filter options from real data ── */
function deriveUniqueValues(stations: RadioStation[], key: keyof RadioStation): string[] {
  const values = new Set<string>();
  stations.forEach((s) => {
    const val = s[key];
    if (val && typeof val === "string") values.add(val);
  });
  return Array.from(values).sort((a, b) => a.localeCompare(b, "es"));
}

const TOOLBAR_ICONS: Record<string, string> = {
  "search": "ri-search-line",
  "category": "ri-price-tag-3-line",
  "country": "ri-earth-line",
  "city": "ri-map-pin-line",
  "language": "ri-translate-2",
  "status": "ri-signal-wifi-line",
};

/* ── Radio Card ── */
function ExploreRadioCard({ station, onPlay }: { station: RadioStation; onPlay: () => void }) {
  const navigate = useNavigate();
  const statusColors: Record<string, { dot: string; label: string; labelColor: string }> = {
    active: { dot: "bg-red-500 animate-pulse", label: "EN VIVO", labelColor: "text-red-600" },
    offline: { dot: "bg-amber-400", label: "OFFLINE", labelColor: "text-amber-600" },
    suspended: { dot: "bg-foreground-400", label: "NO DISPONIBLE", labelColor: "text-foreground-500" },
    error: { dot: "bg-red-500", label: "ERROR", labelColor: "text-red-600" },
    not_configured: { dot: "bg-foreground-300", label: "SIN CONFIG", labelColor: "text-foreground-400" },
    pending_provisioning: { dot: "bg-amber-400", label: "PENDIENTE", labelColor: "text-amber-600" },
    provisioning: { dot: "bg-amber-400", label: "CONFIGURANDO", labelColor: "text-amber-600" },
  };
  const status = statusColors[station.technicalStreamStatus] || statusColors.not_configured;
  const isLive = station.technicalStreamStatus === "active" && station.streamAvailable;

  return (
    <div className="bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl overflow-hidden transition-all duration-200 group">
      {/* Image */}
      <div className="relative w-full aspect-[4/3] overflow-hidden bg-background-100 cursor-pointer" onClick={() => navigate(`/radio/${station.slug}`)}>
        <img
          src={station.logoUrl}
          alt={station.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/40 via-transparent to-transparent"></div>

        {/* Live Status */}
        <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-foreground-950/50 backdrop-blur-sm">
          <span className={`w-2 h-2 rounded-full ${status.dot}`}></span>
          <span className={`text-[8px] font-bold ${status.labelColor}`}>{status.label}</span>
        </div>

        {/* Listeners */}
        {isLive && (
          <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-foreground-950/50 backdrop-blur-sm text-white text-[8px] font-bold">
            <i className="ri-headphone-line text-[8px]"></i>
            {formatCount(station.listeners)}
          </div>
        )}

        {/* Premium badge */}
        {station.isPremium && (
          <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-amber-500/90 text-white text-[8px] font-bold flex items-center gap-1">
            <i className="ri-vip-diamond-fill text-[8px]"></i>
            PREMIUM
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3">
        <button
          onClick={() => navigate(`/radio/${station.slug}`)}
          className="text-left w-full cursor-pointer"
        >
          <h3 className="text-sm font-bold text-foreground-950 truncate group-hover:text-primary-500 transition-colors">{station.name}</h3>
          <p className="text-[10px] text-foreground-500 mt-0.5 truncate">{station.frequency} · {station.category}</p>
        </button>

        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-map-pin-line text-[9px]"></i>
            {station.city}
            {station.country !== "Global" && station.city !== station.country ? `, ${station.country}` : ""}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); onPlay(); }}
            className={`w-7 h-7 rounded-full flex items-center justify-center transition-all cursor-pointer ${
              isLive
                ? "bg-primary-500 hover:bg-primary-600 text-background-50"
                : "bg-background-100 text-foreground-400 cursor-not-allowed"
            }`}
            disabled={!isLive}
          >
            <i className={`text-xs ml-0.5 ${isLive ? "ri-play-fill" : "ri-pause-line"}`}></i>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Tab ── */
export default function AppExploreTab() {
  const { play } = useAudioPlayer();

  /* ── Search & Filters ── */
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("Todas");
  const [country, setCountry] = useState("Todos");
  const [city, setCity] = useState("Todas");
  const [language, setLanguage] = useState("Todos");
  const [status, setStatus] = useState<"Todos" | "live" | "offline">("Todos");
  const [showFilters, setShowFilters] = useState(false);

  /* ── Derived options from real radios data ── */
  const categoryOptions = useMemo(() => {
    const cats = deriveUniqueValues(radioStations, "category");
    return ["Todas", ...cats];
  }, []);

  const countryOptions = useMemo(() => {
    const countries = deriveUniqueValues(radioStations, "country");
    return ["Todos", ...countries];
  }, []);

  const cityOptions = useMemo(() => {
    const cities = deriveUniqueValues(radioStations, "city");
    return ["Todas", ...cities];
  }, []);

  const languageOptions = useMemo(() => {
    const langs = deriveUniqueValues(radioStations, "language");
    return ["Todos", ...langs];
  }, []);

  /* ── Filtered results (combined, all working) ── */
  const filteredResults = useMemo(() => {
    return radioStations.filter((s) => {
      // Name / frequency search
      if (search.trim()) {
        const q = search.toLowerCase().trim();
        const matchesName = s.name.toLowerCase().includes(q);
        const matchesFreq = s.frequency.toLowerCase().includes(q);
        const matchesCity = s.city.toLowerCase().includes(q);
        const matchesCategory = s.category.toLowerCase().includes(q);
        if (!matchesName && !matchesFreq && !matchesCity && !matchesCategory) return false;
      }

      // Category filter
      if (category !== "Todas" && s.category !== category) return false;

      // Country filter
      if (country !== "Todos" && s.country !== country) return false;

      // City filter
      if (city !== "Todas" && s.city !== city) return false;

      // Language filter
      if (language !== "Todos" && s.language !== language) return false;

      // Status filter
      if (status === "live" && !(s.technicalStreamStatus === "active" && s.streamAvailable)) return false;
      if (status === "offline" && s.technicalStreamStatus === "active") return false;

      return true;
    });
  }, [search, category, country, city, language, status]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (category !== "Todas") count++;
    if (country !== "Todos") count++;
    if (city !== "Todas") count++;
    if (language !== "Todos") count++;
    if (status !== "Todos") count++;
    return count;
  }, [category, country, city, language, status]);

  const handlePlay = useCallback((station: RadioStation) => {
    if (station.technicalStreamStatus === "active" && station.streamAvailable) {
      play(station);
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Reproduciendo " + station.name, showConfirmButton: false, timer: 1500, timerProgressBar: true });
    }
  }, [play]);

  const handleClearFilters = useCallback(() => {
    setSearch("");
    setCategory("Todas");
    setCountry("Todos");
    setCity("Todas");
    setLanguage("Todos");
    setStatus("Todos");
  }, []);

  return (
    <div className="h-full overflow-y-auto bg-background-50 scrollbar-none">
      {/* Header */}
      <div className="px-4 md:px-6 pt-4 pb-2">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-rose-100 flex items-center justify-center">
            <i className="ri-compass-3-fill text-rose-500 text-lg"></i>
          </div>
          <div>
            <h1 className="text-lg font-bold text-foreground-950">Explorar</h1>
            <p className="text-[11px] text-foreground-400">Descubrí radios en todo el mundo</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-3">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none">
            <i className={`${search ? "ri-close-line cursor-pointer" : "ri-search-line"}`}></i>
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, frecuencia, ciudad o categoría..."
            className="w-full pl-10 pr-10 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-300 transition-all"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
            >
              <i className="ri-close-circle-fill text-sm"></i>
            </button>
          )}
        </div>

        {/* Filter Toggle */}
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
              showFilters || activeFilterCount > 0
                ? "bg-secondary-500 text-background-50"
                : "bg-background-100 text-foreground-500 hover:bg-background-200"
            }`}
          >
            <i className="ri-equalizer-line text-xs"></i>
            Filtros
            {activeFilterCount > 0 && (
              <span className="w-4 h-4 rounded-full bg-background-50/30 text-[9px] flex items-center justify-center font-bold">{activeFilterCount}</span>
            )}
          </button>

          {/* Quick status toggle */}
          <button
            onClick={() => setStatus(status === "live" ? "Todos" : "live")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
              status === "live" ? "bg-red-500 text-white" : "bg-background-100 text-foreground-500 hover:bg-background-200"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${status === "live" ? "bg-white animate-pulse" : "bg-red-400"}`}></span>
            En Vivo
          </button>

          {/* Active filter chips */}
          {category !== "Todas" && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-[10px] font-semibold whitespace-nowrap">
              {category}
              <button onClick={() => setCategory("Todas")} className="cursor-pointer"><i className="ri-close-line text-xs"></i></button>
            </span>
          )}
          {country !== "Todos" && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-[10px] font-semibold whitespace-nowrap">
              {country}
              <button onClick={() => setCountry("Todos")} className="cursor-pointer"><i className="ri-close-line text-xs"></i></button>
            </span>
          )}
        </div>

        {/* Filter panels */}
        {showFilters && (
          <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5 mb-3 space-y-3">
            {/* Status */}
            <div>
              <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">Estado</p>
              <div className="flex gap-1.5">
                {(["Todos", "live", "offline"] as const).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setStatus(opt)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      status === opt
                        ? opt === "live" ? "bg-red-500 text-white" : opt === "offline" ? "bg-foreground-700 text-background-50" : "bg-foreground-950 text-background-50"
                        : "bg-background-100 text-foreground-500 hover:bg-background-200"
                    }`}
                  >
                    {opt === "Todos" ? "Todos" : opt === "live" ? "En Vivo" : "Offline"}
                  </button>
                ))}
              </div>
            </div>

            {/* Category */}
            <div>
              <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">Categoría</p>
              <div className="flex gap-1.5 flex-wrap">
                {categoryOptions.slice(0, 12).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setCategory(opt)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      category === opt
                        ? "bg-accent-500 text-background-50"
                        : "bg-background-100 text-foreground-500 hover:bg-background-200"
                    }`}
                  >
                    {opt === "Todas" ? "Todas" : opt}
                  </button>
                ))}
              </div>
            </div>

            {/* Country */}
            <div>
              <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">País</p>
              <div className="flex gap-1.5 flex-wrap">
                {countryOptions.slice(0, 10).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setCountry(opt)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      country === opt
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-500 hover:bg-background-200"
                    }`}
                  >
                    {opt === "Todos" ? "Todos" : opt}
                  </button>
                ))}
              </div>
            </div>

            {/* City */}
            <div>
              <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">Ciudad</p>
              <div className="flex gap-1.5 flex-wrap">
                {cityOptions.slice(0, 10).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setCity(opt)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      city === opt
                        ? "bg-secondary-500 text-background-50"
                        : "bg-background-100 text-foreground-500 hover:bg-background-200"
                    }`}
                  >
                    {opt === "Todas" ? "Todas" : opt}
                  </button>
                ))}
              </div>
            </div>

            {/* Language */}
            <div>
              <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">Idioma</p>
              <div className="flex gap-1.5 flex-wrap">
                {languageOptions.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setLanguage(opt)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      language === opt
                        ? "bg-foreground-700 text-background-50"
                        : "bg-background-100 text-foreground-500 hover:bg-background-200"
                    }`}
                  >
                    {opt === "Todos" ? "Todos" : opt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Results count */}
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] text-foreground-500">
            <span className="font-bold text-foreground-700">{filteredResults.length}</span> de {radioStations.length} radios
          </p>
          {activeFilterCount > 0 && (
            <button
              onClick={handleClearFilters}
              className="text-[11px] text-foreground-500 hover:text-foreground-700 font-medium cursor-pointer transition-all"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Results Grid */}
      <div className="px-3 md:px-5 pb-6">
        {filteredResults.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-search-line text-foreground-300 text-2xl"></i>
            </div>
            <p className="text-foreground-600 text-sm font-medium">Sin resultados</p>
            <p className="text-foreground-400 text-xs mt-1">Probá cambiando los filtros</p>
            <button
              onClick={handleClearFilters}
              className="mt-4 px-4 py-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-refresh-line mr-1.5"></i>
              Limpiar todos los filtros
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {filteredResults.map((station) => (
              <ExploreRadioCard
                key={station.id}
                station={station}
                onPlay={() => handlePlay(station)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}